// src/pages/Accounting/Reconcile/components/SetupForm.tsx

import type React from "react";
import { useRef, useEffect, useState } from "react";
import { Calendar as CalendarIcon, AlertCircle } from "lucide-react";
import { Calendar, parseUserDate as parseIsoCal, formatIsoToUserDate } from "../../../../components/Calendar";
import { AccountPicker } from "./AccountPicker";
import { PALETTE } from "../constants";
import type {
  Account,
  Reconciliation,
  SetupFormErrors,
  SetupFormState,
} from "../types";
import {
  currencySymbol,
  formatCurrency,
  formatDateLong,
  isBankLikeAccount,
  parseUserDate,
  validateSetupForm,
} from "../utils";

interface SetupFormProps {
  accounts: Account[];
  state: SetupFormState;
  onStateChange: (next: SetupFormState) => void;
  lastReconciliation: Reconciliation | undefined;
  onStartReconciling: (state: SetupFormState) => void;
  onSaveForLater: (state: SetupFormState) => void;
  fromDraft?: boolean;
}

export function SetupForm({
  accounts, state, onStateChange, lastReconciliation,
  onStartReconciling, onSaveForLater, fromDraft = false,
}: SetupFormProps) {
  const [errors, setErrors] = useState<SetupFormErrors>({});
  const [calendarOpen, setCalendarOpen] = useState(false);
  const dateWrapRef = useRef<HTMLDivElement>(null);

  const current = accounts.find((a) => a.id === state.accountId);
  const bankLike = current ? isBankLikeAccount(current.kind) : true;
  const currency = current?.currency ?? "CAD";
  const symbol = currencySymbol(currency);

  const beginningBalance = lastReconciliation ? lastReconciliation.statementEndingBalance : (current?.currentBalance ?? 0);
  const beginningBalanceDate = lastReconciliation?.periodEnd ?? null;

  const dateLabel = bankLike ? "Statement ending date" : "Ending date";
  const balanceLabelBase = bankLike ? "Statement ending balance" : "Ending balance";
  const balanceLabel = `${balanceLabelBase} (${currency})`;
  const balanceHint = bankLike
    ? "What the bank says your balance is on that date."
    : "The balance from your source document on that date (vendor statement, physical count, or trial balance).";
  const dateHint = bankLike
    ? "The date on your bank statement."
    : "The date on your source document.";
  const beginningMeta = lastReconciliation
    ? `As of ${formatDateLong(lastReconciliation.periodEnd)} \u00b7 carried over from last reconciliation`
    : (bankLike
        ? "No prior reconciliation \u00b7 starts at zero"
        : "No prior reconciliation \u00b7 carried from your general ledger");

  useEffect(() => {
    if (!calendarOpen) return;
    const onClick = (e: MouseEvent) => {
      if (dateWrapRef.current && !dateWrapRef.current.contains(e.target as Node)) {
        setCalendarOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [calendarOpen]);

  const updateField = <K extends keyof SetupFormState>(key: K, value: SetupFormState[K]) => {
    onStateChange({ ...state, [key]: value });
    if (errors[key as keyof SetupFormErrors]) {
      setErrors({ ...errors, [key]: undefined });
    }
  };

  const handleCalendarSelect = (iso: string) => {
    updateField("statementEndingDate", formatIsoToUserDate(iso));
    setCalendarOpen(false);
  };

  const handleStart = () => {
    const nextErrors = validateSetupForm(state, beginningBalanceDate, bankLike);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      onStartReconciling(state);
    }
  };

  const dateIso = parseUserDate(state.statementEndingDate);
  const hasDateError = !!errors.statementEndingDate;
  const hasBalanceError = !!errors.statementEndingBalance;

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={stepLabelStyle}>Set up this reconciliation</div>

      <div style={{ ...fieldStyle, maxWidth: 440 }}>
        <label style={labelStyle}>Which account</label>
        <AccountPicker accounts={accounts} valueId={state.accountId} onChange={(id) => updateField("accountId", id)} />
      </div>

      {!bankLike && (
        <div style={adaptNoteStyle}>
          <strong style={{ color: PALETTE.ink, fontWeight: 600 }}>No bank statement for this account.</strong>
          {" "}Reconcile it against your source document: a vendor statement, physical count, trial balance, or spreadsheet. Enter the balance that document shows on your chosen date.
        </div>
      )}

      <div style={rowStyle}>
        <div style={{ ...fieldTight }}>
          <div style={labelStyle}>Beginning balance ({currency})</div>
          <div style={lockedValueStyle}>
            {formatCurrency(beginningBalance, currency)}
            <div style={lockedMetaStyle}>{beginningMeta}</div>
          </div>
        </div>
        <div style={{ ...fieldTight, position: "relative" }} ref={dateWrapRef}>
          <label style={{ ...labelStyle, color: hasDateError ? PALETTE.redDark : PALETTE.ink2 }}>
            {dateLabel}
            {fromDraft && state.statementEndingDate && <span style={draftBadgeStyle}>&bull; From draft</span>}
          </label>
          <div style={{ ...boxedStyle, borderColor: hasDateError ? PALETTE.red : PALETTE.line, background: hasDateError ? PALETTE.redBg : "#fff" }}>
            <input
              type="text"
              placeholder="dd/mm/yyyy"
              value={state.statementEndingDate}
              onChange={(e) => updateField("statementEndingDate", e.target.value)}
              onFocus={() => setCalendarOpen(true)}
              style={innerInputStyle}
            />
            <button type="button" onClick={() => setCalendarOpen((v) => !v)} style={iconBtnStyle} aria-label="Open calendar">
              <CalendarIcon size={16} color={PALETTE.ink3} />
            </button>
          </div>
          {hasDateError ? (
            <div style={errHintStyle}>
              <AlertCircle size={13} />
              {errors.statementEndingDate}
            </div>
          ) : (
            <div style={hintStyle}>{dateHint}</div>
          )}
          {calendarOpen && (
            <Calendar
              valueIso={dateIso ?? undefined}
              onSelect={handleCalendarSelect}
              onClear={() => { updateField("statementEndingDate", ""); setCalendarOpen(false); }}
            />
          )}
        </div>
      </div>

      <div style={fieldTight}>
        <label style={{ ...labelStyle, color: hasBalanceError ? PALETTE.redDark : PALETTE.ink2 }}>
          {balanceLabel}
          {fromDraft && state.statementEndingBalance && <span style={draftBadgeStyle}>&bull; From draft</span>}
        </label>
        <div style={{ ...boxedStyle, borderColor: hasBalanceError ? PALETTE.red : PALETTE.line, background: hasBalanceError ? PALETTE.redBg : "#fff" }}>
          <input
            type="text"
            placeholder={`${symbol}0.00`}
            value={state.statementEndingBalance}
            onChange={(e) => updateField("statementEndingBalance", e.target.value)}
            style={innerInputStyle}
          />
        </div>
        {hasBalanceError ? (
          <div style={errHintStyle}>
            <AlertCircle size={13} />
            {errors.statementEndingBalance}
          </div>
        ) : (
          <div style={hintStyle}>{balanceHint}</div>
        )}
      </div>

      <div style={ctaRowStyle}>
        <button type="button" onClick={handleStart} style={btnDarkStyle}>
          Start reconciling &rarr;
        </button>
        <button type="button" onClick={() => onSaveForLater(state)} style={btnLinkStyle}>
          Save for later
        </button>
      </div>
    </div>
  );
}

// Silence unused import in case Calendar's parseUserDate is needed later elsewhere
void parseIsoCal;

const stepLabelStyle: React.CSSProperties = { fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: PALETTE.ink2, fontWeight: 700, margin: "0 0 16px" };
const fieldStyle: React.CSSProperties = { marginBottom: 20 };
const fieldTight: React.CSSProperties = { marginBottom: 20, width: 280 };
const labelStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: PALETTE.ink2, fontWeight: 600, marginBottom: 7 };
const draftBadgeStyle: React.CSSProperties = { fontSize: 10.5, color: PALETTE.ink3, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", marginLeft: 2 };
const boxedStyle: React.CSSProperties = { width: "100%", padding: "11px 14px", border: `1px solid ${PALETTE.line}`, borderRadius: 8, background: "#fff", fontSize: 14.5, color: PALETTE.ink, fontWeight: 500, fontFamily: "inherit", outline: "none", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 };
const innerInputStyle: React.CSSProperties = { border: 0, outline: "none", flex: 1, fontSize: 14.5, color: PALETTE.ink, fontWeight: 500, fontFamily: "inherit", background: "transparent", minWidth: 0 };
const iconBtnStyle: React.CSSProperties = { background: "transparent", border: 0, padding: 0, cursor: "pointer", display: "flex", alignItems: "center", flexShrink: 0 };
const hintStyle: React.CSSProperties = { fontSize: 12.5, color: PALETTE.ink3, fontWeight: 500, marginTop: 7 };
const errHintStyle: React.CSSProperties = { fontSize: 12.5, color: PALETTE.red, fontWeight: 600, marginTop: 7, display: "flex", alignItems: "center", gap: 6 };
const rowStyle: React.CSSProperties = { display: "flex", gap: 24 };
const lockedValueStyle: React.CSSProperties = { padding: "11px 14px", fontSize: 14.5, color: PALETTE.ink, fontWeight: 600, fontVariantNumeric: "tabular-nums", border: `1px solid ${PALETTE.lineSoft}`, borderRadius: 8, background: PALETTE.bgFade };
const lockedMetaStyle: React.CSSProperties = { fontSize: 11.5, color: PALETTE.ink3, fontWeight: 500, marginTop: 4, letterSpacing: 0, textTransform: "none" };
const adaptNoteStyle: React.CSSProperties = { fontSize: 12.5, color: PALETTE.ink3, fontWeight: 500, margin: "-4px 0 18px", padding: "10px 14px", borderLeft: `2px solid ${PALETTE.ink3}`, background: PALETTE.bgFade, borderRadius: "0 6px 6px 0", maxWidth: 760, lineHeight: 1.5 };
const ctaRowStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: 16, marginTop: 28 };
const btnDarkStyle: React.CSSProperties = { background: "#0E1A1A", color: "#fff", border: 0, fontWeight: 600, fontSize: 13.5, padding: "11px 20px", borderRadius: 10, cursor: "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" };
const btnLinkStyle: React.CSSProperties = { background: "transparent", border: 0, color: PALETTE.ink2, fontWeight: 500, fontSize: 13.5, padding: "11px 8px", cursor: "pointer" };